import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Grid, TextField, Button, CircularProgress, Alert, IconButton, Breadcrumbs, Link, Switch, FormControlLabel, InputAdornment
} from '@mui/material';
import { CloudUpload, Save, ArrowBack, Delete, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { createFoodVendor, getFoodVendorById, updateFoodVendor } from '../../services/Api';

const CreateAndModifyFoodVendor = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(isEdit);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [shopNameError, setShopNameError] = useState('');
    const [mobileError, setMobileError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [addressError, setAddressError] = useState('');
    const [pincodeError, setPincodeError] = useState('');
    const [ownerNameError, setOwnerNameError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [cityError, setCityError] = useState('');
    const [stateError, setStateError] = useState('');
    const [landmarkError, setLandmarkError] = useState('');
    const [addressLine1Error, setAddressLine1Error] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [formData, setFormData] = useState({
        shopName: '',
        ownerName: '',
        mobile: '',
        email: '',
        password: '',
        isActive: true,
        deliveryTime: '',
        address: {
            id: '',
            pincode: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            latitude: '',
            longitude: '',
            state: '',
            label: '',
            landmark: '',
        },
    });
    const [profileImage, setProfileImage] = useState(null);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (isEdit) {
            fetchVendorDetails();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchVendorDetails = async () => {
        try {
            const response = await getFoodVendorById(id);
            if (response.success || response.data) {
                const data = response.data || response;
                const primaryAddress = data.addresses && data.addresses.length > 0 ? data.addresses[0] : {};
                setFormData({
                    shopName: data.shopName || '',
                    ownerName: data.ownerName || '',
                    mobile: data.mobile || '',
                    email: data.email || '',
                    password: '', // Leave password empty on edit unless changing
                    isActive: data.isActive !== undefined ? data.isActive : true,
                    deliveryTime: data.deliveryTime || '',
                    address: {
                        id: primaryAddress.id || '',
                        pincode: primaryAddress.pincode || '',
                        addressLine1: primaryAddress.addressLine1 || '',
                        addressLine2: primaryAddress.addressLine2 || '',
                        city: primaryAddress.city || '',
                        latitude: primaryAddress.latitude || '',
                        longitude: primaryAddress.longitude || '',
                        state: primaryAddress.state || '',
                        label: primaryAddress.label || '',
                        landmark: primaryAddress.landmark || '',
                    }
                });
                if (data.image) {
                    setPreview(data.image);
                }
            } else {
                setError(response?.message || 'Failed to fetch vendor details');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Error fetching food vendor data');
            console.error(err);
        } finally {
            setFetchLoading(false);
        }
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event) => event.preventDefault();

    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;

        if (name === 'mobile') {
            const digits = value.replace(/\D/g, '')
            setFormData(prev => ({ ...prev, mobile: digits }));
            if (digits.length > 0 && digits.length !== 10) {
                setMobileError('Mobile number must be 10 digits');
            } else {
                setMobileError('');
            }
            return;
        }

        if (name === 'deliveryTime') {
            const digits = value.replace(/\D/g, '');
            setFormData(prev => ({ ...prev, deliveryTime: digits }));
            return;
        }

        if (name === 'address.pincode') {
            const hasAlphabet = /[a-zA-Z]/.test(value);
            if (hasAlphabet) {
                setPincodeError('Pincode must contain only numbers');
            } else {
                setPincodeError('');
            }
            const digits = value.replace(/\D/g, '').slice(0, 6);
            setFormData((prev) => ({
                ...prev,
                address: { ...prev.address, pincode: digits },
            }));
            return;
        }

        if (name === 'address.city') {
            const hasNumber = /\d/.test(value);
            if (hasNumber) {
                setCityError('City must contain only letters');
            } else {
                setCityError('');
            }
            const cleanValue = value.replace(/\d/g, '');
            setFormData(prev => ({
                ...prev,
                address: { ...prev.address, city: cleanValue }
            }));
            return;
        }

        if (name === 'address.state') {
            const hasNumber = /\d/.test(value);
            if (hasNumber) {
                setStateError('State must contain only letters');
            } else {
                setStateError('');
            }
            const cleanValue = value.replace(/\d/g, '');
            setFormData(prev => ({
                ...prev,
                address: { ...prev.address, state: cleanValue }
            }));
            return;
        }

        if (name === 'address.landmark') {
            const hasSpecial = /[^a-zA-Z0-9\s]/.test(value);
            if (hasSpecial) {
                setLandmarkError('Landmark must not contain special characters');
            } else {
                setLandmarkError('');
            }
            const cleanValue = value.replace(/[^a-zA-Z0-9\s]/g, '');
            setFormData(prev => ({
                ...prev,
                address: { ...prev.address, landmark: cleanValue }
            }));
            return;
        }

        if (name === 'shopName') {
            // if (hasInvalid) {
            //     setShopNameError('Vendor name should be in alphabets only');
            // } else {
            //     setShopNameError('');
            // }
            setFormData(prev => ({ ...prev, shopName: value }));
            return;
        }

        if (name === 'ownerName') {
            const hasInvalid = /[^a-zA-Z\s]/.test(value);
            if (hasInvalid) {
                setOwnerNameError('Owner name should be in alphabets only');
            } else {
                setOwnerNameError('');
            }
            setFormData(prev => ({ ...prev, ownerName: value }));
            return;
        }

        if (name === 'email') {
            setEmailError('');
            setFormData(prev => ({ ...prev, email: value }));
            return;
        }

        if (name === 'password') setPasswordError('');
        if (name.startsWith('address.')) {
            setAddressError('');
            if (name === 'address.addressLine1') setAddressLine1Error('');
        }

        if (name === 'isActive') {
            setFormData((prev) => ({ ...prev, [name]: checked }));
            return;
        }

        if (name.startsWith('address.')) {
            const addressField = name.split('.')[1];
            setFormData((prev) => ({
                ...prev,
                address: { ...prev.address, [addressField]: value },
            }));
        } else if (type === 'checkbox') {
            setFormData((prev) => ({ ...prev, [name]: checked }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setError('Only image files are allowed');
                setProfileImage(null);
                setPreview(null);
                return;
            }
            setError(null);
            setProfileImage(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        let hasErrors = false;

        // Reset errors
        setShopNameError('');
        setMobileError('');
        setPasswordError('');
        setAddressError('');
        setAddressLine1Error('');
        setPincodeError('');
        setOwnerNameError('');
        setEmailError('');
        setCityError('');
        setStateError('');
        setLandmarkError('');
        setError(null);

        // Client-side validation
        if (!isEdit && !profileImage) {
            setError('The image is required');
            hasErrors = true;
        } else if (profileImage && !profileImage.type.startsWith('image/')) {
            setError('Only image files are allowed');
            hasErrors = true;
        }

        if (!formData.shopName.trim()) {
            setShopNameError('Vendor name is required');
            hasErrors = true;
        }

        if (!formData.mobile.trim()) {
            setMobileError('Mobile number is required');
            hasErrors = true;
        } else if (formData.mobile.length !== 10) {
            setMobileError('Mobile number must be exactly 10 digits');
            hasErrors = true;
        }

        if (!formData.address.pincode.trim()) {
            setPincodeError('Pincode is required');
            hasErrors = true;
        } else if (formData.address.pincode.length !== 6) {
            setPincodeError('Pincode must be exactly 6 digits');
            hasErrors = true;
        }

        if (!formData.address.addressLine1.trim()) {
            setAddressLine1Error('Address Line 1 is required');
            hasErrors = true;
        }

        if (!formData.ownerName.trim()) {
            setOwnerNameError('Owner name is required');
            hasErrors = true;
        } else if (/[^a-zA-Z\s]/.test(formData.ownerName)) {
            setOwnerNameError('Owner name should be in alphabets only');
            hasErrors = true;
        }

        if (!formData.email.trim()) {
            setEmailError('Email is required');
            hasErrors = true;
        }

        if (!isEdit && !formData.password) {
            setPasswordError('Password is required');
            hasErrors = true;
        }

        if (!formData.address.city.trim()) {
            setCityError('City is required');
            hasErrors = true;
        } else if (/\d/.test(formData.address.city)) {
            setCityError('City must contain only letters');
            hasErrors = true;
        }

        if (!formData.address.state.trim()) {
            setStateError('State is required');
            hasErrors = true;
        } else if (/\d/.test(formData.address.state)) {
            setStateError('State must contain only letters');
            hasErrors = true;
        }

        if (formData.address.landmark && /[^a-zA-Z0-9\s]/.test(formData.address.landmark)) {
            setLandmarkError('Landmark must not contain special characters');
            hasErrors = true;
        }

        if (hasErrors) {
            const isAllEmpty = !formData.shopName.trim() &&
                !formData.ownerName.trim() &&
                !formData.mobile.trim() &&
                !formData.email.trim() &&
                (!isEdit ? !formData.password : true) &&
                !formData.address.pincode.trim() &&
                !formData.address.addressLine1.trim() &&
                !formData.address.city.trim() &&
                !formData.address.state.trim();

            if (isAllEmpty) {
                setError('please fill all the fields');
            }
            return;
        }

        setLoading(true);
        setSuccess(false);

        const data = new FormData();
        data.append('shopName', formData.shopName);
        data.append('ownerName', formData.ownerName);
        data.append('mobile', formData.mobile);
        data.append('email', formData.email);
        if (formData.password) {
            data.append('password', formData.password);
        }
        data.append('isActive', formData.isActive);
        data.append('deliveryTime', formData.deliveryTime);

        Object.keys(formData.address).forEach(key => {
            let value = formData.address[key];
            if (!value) {
                if (key === 'latitude' || key === 'longitude') value = '0';
                else if (key === 'landmark') value = 'Null';
                else if (key === 'label' || key === 'addressLine2') value = 'N/A';
                else value = '';
            }
            if (value !== '') {
                data.append(`address[${key}]`, value);
            }
        });

        if (profileImage) {
            data.append('profileImage', profileImage);
        }

        try {
            const response = isEdit ? await updateFoodVendor(id, data) : await createFoodVendor(data);
            if (response.success) {
                setSuccess(true);
                setTimeout(() => navigate('/vendors/food'), 1500);
            } else {
                const msg = response.message || `Failed to ${isEdit ? 'update' : 'create'} vendor`;
                setError(msg);
                if (msg.toLowerCase().includes('password')) setPasswordError(msg);
                if (msg.toLowerCase().includes('address')) setAddressError(msg);
                if (msg.toLowerCase().includes('email')) setEmailError(msg);
            }
        } catch (err) {
            const msg = err.response?.data?.message || `An error occurred while ${isEdit ? 'updating' : 'creating'} the vendor`;
            setError(msg);

            // Map server errors
            if (msg.toLowerCase().includes('mobile')) setMobileError(msg);
            if (msg.toLowerCase().includes('password')) setPasswordError(msg);
            if (msg.toLowerCase().includes('address')) setAddressError(msg);
            if (msg.toLowerCase().includes('email')) setEmailError(msg);

            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (fetchLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/vendors/food');
                        }}
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <ArrowBack sx={{ mr: 0.5, fontSize: 'inherit' }} />
                        Food Vendors
                    </Link>
                    <Typography color="text.primary">{isEdit ? 'Edit Food Vendor' : 'Create Food Vendor'}</Typography>
                </Breadcrumbs>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {isEdit ? 'Modify Food Vendor' : 'Create New Food Vendor'}
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'text.border' }}>
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 3 }}>Food Vendor {isEdit ? 'updated' : 'created'} successfully! Redirecting...</Alert>}

                <form onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                                Profile Image
                            </Typography>
                            <Box
                                sx={{
                                    border: '2px dashed',
                                    borderColor: 'text.border',
                                    borderRadius: 2,
                                    height: 250,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    backgroundColor: 'rgba(0,0,0,0.02)',
                                    position: 'relative',
                                }}
                            >
                                {preview ? (
                                    <>
                                        <Box component="img" src={preview} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <IconButton
                                            sx={{
                                                position: 'absolute', top: 8, right: 8,
                                                backgroundColor: 'rgba(255,255,255,0.8)',
                                                '&:hover': { backgroundColor: 'white' },
                                            }}
                                            onClick={() => { setProfileImage(null); setPreview(null); }}
                                        >
                                            <Delete color="error" />
                                        </IconButton>
                                    </>
                                ) : (
                                    <>
                                        <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                                        <Typography variant="body2" color="text.secondary">Upload Profile Image</Typography>
                                    </>
                                )}
                                <input
                                    id="file-input-food-vendor"
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={(e) => {
                                        handleImageChange(e);
                                        e.target.value = null;
                                    }}
                                />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                                Note: Image size must be 300px * 300px
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Button
                                    variant="outlined"
                                    sx={{ mt: 2, fontSize: "10px !important" }}
                                    startIcon={<CloudUpload />}
                                    onClick={() => document.getElementById("file-input-food-vendor").click()}
                                >
                                    Upload Image
                                </Button>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 8 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Food Vendor Details</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch checked={formData.isActive} onChange={handleInputChange} name="isActive" color="primary" />
                                    }
                                    label="Active Status"
                                />
                            </Box>

                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField size="small"
                                        fullWidth
                                        label="Shop Name"
                                        name="shopName"
                                        value={formData.shopName}
                                        onChange={handleInputChange}
                                        required
                                        error={!!shopNameError}
                                        helperText={shopNameError}
                                        placeholder="e.g. Burger Palace"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField size="small"
                                        fullWidth
                                        label="Owner Name"
                                        name="ownerName"
                                        value={formData.ownerName}
                                        onChange={handleInputChange}
                                        required
                                        error={!!ownerNameError}
                                        helperText={ownerNameError}
                                        placeholder="e.g. Jane Doe"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField size="small"
                                        fullWidth
                                        label="Mobile"
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleInputChange}
                                        required
                                        error={!!mobileError}
                                        helperText={mobileError}
                                        placeholder="e.g. 1234567890"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField size="small"
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        error={!!emailError}
                                        helperText={emailError}
                                        placeholder="e.g. info@example.com"
                                    />
                                </Grid>
                                  <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField size="small"
                                        fullWidth
                                        label="Delivery Time (minutes)"
                                        name="deliveryTime"
                                        type="number"
                                        value={formData.deliveryTime}
                                        onChange={handleInputChange}
                                     
                                        inputProps={{ min: 0 }}
                                        placeholder="e.g. 45"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField size="small"
                                        fullWidth
                                        label="Password"
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        required={!isEdit}
                                        error={!!passwordError}
                                        helperText={passwordError || (isEdit ? "" : "Min. 8 characters (Uppercase, Lowercase, Number, & Special Character)")}
                                        placeholder={isEdit ? "Leave blank to keep current" : "Enter password"}
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                            onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                        >
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                           

                            <Typography variant="subtitle1" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>Address Details</Typography>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField size="small"
                                        fullWidth
                                        label="Pincode"
                                        name="address.pincode"
                                        value={formData.address.pincode}
                                        onChange={handleInputChange}
                                        required
                                        error={!!pincodeError || !!addressError}
                                        helperText={pincodeError || addressError}
                                        placeholder="e.g. 123456"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField size="small"
                                        fullWidth
                                        label="Address Line 1"
                                        name="address.addressLine1"
                                        value={formData.address.addressLine1}
                                        onChange={handleInputChange}
                                        required
                                        error={!!addressLine1Error || !!addressError}
                                        helperText={addressLine1Error || addressError}
                                        placeholder="e.g. 123 Main St"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField size="small" fullWidth label="Address Line 2" name="address.addressLine2" value={formData.address.addressLine2} onChange={handleInputChange} placeholder="e.g. Apt 4B" />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField size="small"
                                        fullWidth
                                        label="City"
                                        name="address.city"
                                        value={formData.address.city}
                                        onChange={handleInputChange}
                                        required
                                        error={!!cityError || !!addressError}
                                        helperText={cityError || addressError}
                                        placeholder="e.g. New York"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField size="small"
                                        fullWidth
                                        label="State"
                                        name="address.state"
                                        value={formData.address.state}
                                        onChange={handleInputChange}
                                        required
                                        error={!!stateError || !!addressError}
                                        helperText={stateError || addressError}
                                        placeholder="e.g. NY"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField size="small" fullWidth label="Label" name="address.label" value={formData.address.label} onChange={handleInputChange} placeholder="e.g. Kitchen" />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField size="small"
                                        fullWidth
                                        label="Landmark"
                                        name="address.landmark"
                                        value={formData.address.landmark}
                                        onChange={handleInputChange}
                                        error={!!landmarkError}
                                        helperText={landmarkError}
                                        placeholder="e.g. Near Park"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField size="small" fullWidth label="Latitude" name="address.latitude" value={formData.address.latitude} onChange={handleInputChange} placeholder="e.g. 40.7128" />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField size="small" fullWidth label="Longitude" name="address.longitude" value={formData.address.longitude} onChange={handleInputChange} placeholder="e.g. -74.0060" />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Note: For longitude and latitude please click the link <Link href="https://www.latlong.net/" target="_blank" rel="noopener noreferrer">https://www.latlong.net/</Link>
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 5, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                <Button variant="outlined" onClick={() => navigate('/vendors/food')} disabled={loading} sx={{ px: 4 }}>Cancel</Button>
                                <Button type="submit" variant="contained" startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />} disabled={loading} sx={{ px: 6, backgroundColor: 'primary.main' }}>
                                    {isEdit ? 'Save Changes' : 'Create Vendor'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
};

export default CreateAndModifyFoodVendor;
